import React from 'react';
import { Link } from 'react-router-dom';
import { Car, Award, Users, MapPin, ArrowRight, Star, CheckCircle, Facebook } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { businessInfo, services, testimonials } from '../mock';

export const Home = () => {
  const featuredServices = services.slice(0, 3);
  
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-gray-50 to-gray-100 py-20">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-4xl mx-auto">
            <div className="mb-8">
              <div className="text-6xl md:text-8xl lg:text-9xl font-black text-black mb-6 tracking-wider leading-tight">
                <div className="mb-2">DRIVE</div>
                <div className="flex items-center justify-center">
                  <span>INVERC</span>
                  <span className="text-red-600">L</span>
                  <span>YDE</span>
                </div>
              </div>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              {businessInfo.tagline}
            </h1>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Learn to drive with confidence from a {businessInfo.qualification}. 
              Professional driving lessons starting from £40.
              Covering Wemyss Bay, Skelmorlie, Gourock, Greenock.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button className="bg-red-600 hover:bg-red-700 text-white px-8 py-4 text-lg font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all">
                  Book Your First Lesson
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/services">
                <Button variant="outline" className="border-red-600 text-red-600 hover:bg-red-50 px-8 py-4 text-lg font-semibold rounded-lg">
                  View All Services
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Why Choose Drive Inverclyde?</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Professional driving instruction with a proven track record of success
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="font-semibold text-lg mb-2">DVSA Qualified</h3>
              <p className="text-gray-600 text-sm">Fully qualified and approved instructor</p>
            </div>
            
            <div className="text-center">
              <div className="bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Car className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="font-semibold text-lg mb-2">Automatic Training</h3>
              <p className="text-gray-600 text-sm">Specialist in automatic transmission</p>
            </div>
            
            <div className="text-center">
              <div className="bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <MapPin className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="font-semibold text-lg mb-2">Local Coverage</h3>
              <p className="text-gray-600 text-sm">Serving all of Inverclyde area (Excl Port Glasgow)</p>
            </div>
            
            <div className="text-center">
              <div className="bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="font-semibold text-lg mb-2">Patient Approach</h3>
              <p className="text-gray-600 text-sm">Supportive learning environment</p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Services</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Comprehensive driving instruction tailored to your needs
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {featuredServices.map((service) => (
              <Card key={service.id} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <h3 className="font-bold text-xl mb-3 text-gray-900">{service.name}</h3>
                  <p className="text-gray-600 mb-4">{service.description}</p>
                  <div className="mb-4">
                    <span className="text-2xl font-bold text-red-600">{service.price}</span>
                    <span className="text-gray-500 ml-2">{service.duration}</span>
                  </div>
                  <ul className="space-y-2 mb-6">
                    {service.features.slice(0, 3).map((feature, index) => (
                      <li key={index} className="flex items-center text-sm text-gray-600">
                        <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <Link to="/contact">
                    <Button className="w-full bg-red-600 hover:bg-red-700 text-white">
                      Book Now
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
          
          <div className="text-center mt-8">
            <Link to="/services">
              <Button variant="outline" className="border-red-600 text-red-600 hover:bg-red-50">
                View All Services
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">What Some Previous Students Say About Drive Inverclyde</h2>
            <p className="text-gray-600">Real feedback from successful drivers</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="border-l-4 border-l-red-600 hover:shadow-xl transition-all">
                <CardContent className="p-6">
                  {/* Photo section for real testimonials */}
                  {testimonial.photo && (
                    <div className="mb-4 flex justify-center">
                      <img 
                        src={testimonial.photo} 
                        alt={`${testimonial.name} - Passed Driving Test`}
                        className="w-24 h-24 rounded-full object-cover border-4 border-red-100 shadow-lg"
                      />
                    </div>
                  )}
                  
                  <div className="flex mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                    ))}
                  </div>
                  
                  {/* Test result badge for real testimonials */}
                  {testimonial.result && (
                    <div className="mb-3">
                      <span className="bg-green-100 text-green-800 text-xs font-semibold px-3 py-1 rounded-full">
                        ✅ {testimonial.result}
                      </span>
                    </div>
                  )}
                  
                  <p className="text-gray-600 mb-4 italic leading-relaxed">"{testimonial.text}"</p>
                  <div>
                    <p className="font-semibold text-gray-900">{testimonial.name}</p>
                    <p className="text-sm text-gray-500">{testimonial.location}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-red-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Start Driving?</h2>
          <p className="text-xl mb-8 text-red-100">
            Contact us today to book your first lesson and begin your journey to driving independence.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="bg-white text-red-600 hover:bg-gray-100 px-8 py-4 text-lg font-semibold">
                Book Your Lesson
              </Button>
            </Link>
            <a href={`tel:${businessInfo.phone}`}>
              <Button variant="outline" className="border-white text-white hover:bg-white hover:text-red-600 px-8 py-4 text-lg font-semibold">
                Call Now: {businessInfo.phone}
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;